import { RefObject, useEffect, useState } from 'react';

import {
  getSharedDCCTVolumeInfo,
  MedicalImageRender,
  type ToothSliceGeometryMode,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { debug } from '@/shared/lib';

import {
  readFullHeadMprPaneLayout,
  type DCCTVolumeState,
} from '@/features/MPR';

import {
  FULL_HEAD_MPR_ERROR_CODES,
  type FullHeadMPRErrorCode,
  type FullHeadMPREngineSession,
  type FullHeadMPRToothModeState,
} from '../config';
import type { ToothSliceModeScope } from '../lib';
import { resolveToothSliceThicknessOptionsMm } from '../lib';

import { useResolvedToothID } from './useResolvedToothID';
import { useToothSliceFocus } from './useToothSliceFocus';
import { useToothSliceMode } from './useToothSliceMode';
import { useToothSliceOptions } from './useToothSliceOptions';

type UseFullHeadMPRParams = {
  containerRef: RefObject<HTMLDivElement | null>;
  isActive: boolean;
  volumeState: DCCTVolumeState;
  sliceModeScope: ToothSliceModeScope;
  defaultGeometryMode?: ToothSliceGeometryMode;
  toothID?: string;
};

type UseFullHeadMPRResult = {
  errorMessage: FullHeadMPRErrorCode | null;
  isReady: boolean;
  toothMode: FullHeadMPRToothModeState;
  resetView: () => void;
  retry: () => void;
  setToothSliceGeometryMode: (mode: ToothSliceGeometryMode) => void;
};

const noop = () => undefined;

export const useFullHeadMPR = (
  params: UseFullHeadMPRParams,
): UseFullHeadMPRResult => {
  const {
    containerRef,
    isActive,
    volumeState,
    sliceModeScope,
    defaultGeometryMode,
    toothID,
  } = params;

  const [engineSession, setEngineSession] =
    useState<FullHeadMPREngineSession | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<FullHeadMPRErrorCode | null>(
    null,
  );

  useEffect(() => {
    if (!isActive) {
      setErrorMessage(null);
      setEngineSession(null);
      return;
    }

    if (volumeState.status === 'pending') {
      setErrorMessage(null);
      setEngineSession(null);
      return;
    }

    if (volumeState.status === 'missing') {
      setErrorMessage(FULL_HEAD_MPR_ERROR_CODES.missingVolume);
      setEngineSession(null);
      return;
    }

    if (volumeState.status === 'failed') {
      setErrorMessage(FULL_HEAD_MPR_ERROR_CODES.dataStream);
      setEngineSession(null);
      return;
    }

    const volumeFileID = volumeState.volumeFileID;

    if (!volumeFileID) {
      setErrorMessage(FULL_HEAD_MPR_ERROR_CODES.startViewer);
      setEngineSession(null);
      return;
    }

    if (!containerRef.current) {
      return;
    }

    let isDisposed = false;
    const removeView =
      MedicalImageRender.addFullHeadMPRView?.(true, {
        retainVolume: true,
      }) || noop;

    setErrorMessage(null);
    setEngineSession(null);

    MedicalImageRender.setViewContainer('fullHeadMPR', containerRef);

    const removeReadyListener =
      MedicalImageRender.addEventListener(
        'fullHeadMPRReady',
        (event) => {
          if (!isDisposed && event.volumeFileID === volumeFileID) {
            setEngineSession({ id: crypto.randomUUID(), volumeFileID });
          }
        },
        'fullHeadMPR',
      ) || noop;
    const removeErrorListener =
      MedicalImageRender.addEventListener(
        'fullHeadMPRError',
        (event) => {
          if (!isDisposed) {
            debug('[FullHeadMPR] Render failed', event.message);
            setErrorMessage(FULL_HEAD_MPR_ERROR_CODES.startViewer);
          }
        },
        'fullHeadMPR',
      ) || noop;

    MedicalImageRender.setFullHeadMPRPaneLayout(readFullHeadMprPaneLayout());

    void MedicalImageRender.startFullHeadMPR({
      volumeFileID,
    }).catch((error: unknown) => {
      if (isDisposed) {
        return;
      }
      debug('[FullHeadMPR] Failed to start', error);
      setErrorMessage(FULL_HEAD_MPR_ERROR_CODES.startViewer);
    });

    return () => {
      isDisposed = true;
      removeReadyListener();
      removeErrorListener();
      MedicalImageRender.disposeFullHeadMPR();
      removeView();
    };
  }, [
    containerRef,
    isActive,
    retryCount,
    volumeState.status,
    volumeState.volumeFileID,
  ]);

  const activeEngineSession =
    isActive &&
    volumeState.status === 'ready' &&
    engineSession?.volumeFileID === volumeState.volumeFileID
      ? engineSession
      : null;

  const { setGeometryMode, toothMode } = useToothSliceMode({
    isReady: Boolean(activeEngineSession),
    scope: sliceModeScope,
    defaultGeometryMode,
  });

  const { isReady, isFailed, resetFocus } = useToothSliceFocus({
    engineSession: activeEngineSession,
    toothID,
  });
  const focusToothID = useResolvedToothID(toothID);
  const thicknessOptionsMm = resolveToothSliceThicknessOptionsMm(
    activeEngineSession
      ? getSharedDCCTVolumeInfo(activeEngineSession.volumeFileID)
      : null,
  );
  const { sliceOptions } = useToothSliceOptions({
    thicknessOptionsMm,
    toothID: focusToothID,
  });

  useEffect(() => {
    if (!activeEngineSession || !isReady) {
      return;
    }

    MedicalImageRender.setFullHeadMPRSliceThicknessMm(
      focusToothID
        ? {
            axial: sliceOptions.axial.thicknessMm,
            coronal: sliceOptions.frontal.thicknessMm,
            sagittal: sliceOptions.sagittal.thicknessMm,
          }
        : {},
    );
  }, [
    activeEngineSession,
    focusToothID,
    isReady,
    sliceOptions.axial.thicknessMm,
    sliceOptions.frontal.thicknessMm,
    sliceOptions.sagittal.thicknessMm,
  ]);

  const resolvedError =
    errorMessage ?? (isFailed ? FULL_HEAD_MPR_ERROR_CODES.dataStream : null);

  const retry = () => {
    if (resolvedError === FULL_HEAD_MPR_ERROR_CODES.dataStream) {
      window.location.reload();
      return;
    }

    setEngineSession(null);
    setRetryCount((current) => current + 1);
  };

  return {
    errorMessage: resolvedError,
    isReady: isReady && !resolvedError,
    resetView: resetFocus,
    retry,
    setToothSliceGeometryMode: setGeometryMode,
    toothMode,
  };
};
